import React from 'react';
import { cx } from '../cx';

// 단계 막대 — 한 칸이 1단계인 N칸 막대 (D-07 §3.2 #20 예외로 승인된 신규 컴포넌트, 승인 N-01).
// 칸별 상태: 끝남(done) = 포인트색 · 지금(current) = tone 색(danger 빨강 · warning 노랑 · default 기본 회색) · 예정 = 기본 회색.
// 칸 번호는 앞에서부터 센다. 앞의 `done` 칸이 끝남, 바로 다음 칸이 지금(current=false 면 지금 칸 없음).
// 색은 토큰만 쓴다(--gt-primary · --signal-red · --signal-yellow · --shell-border). 점·글자와 함께 쓰고, 막대 단독으로 의미를 전하지 않는다.
// contract 없는 손 작성 컴포넌트(Drawer.tsx와 동일 예외) — 클래스는 styles/components.css(.stage-bar).
export type StageBarTone = 'default' | 'danger' | 'warning';

export interface StageBarProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** 전체 칸 수(단계 수). 1 이상의 정수 */
  total: number;
  /** 끝난 칸 수 — 0~total 로 맞춰 쓴다 */
  done: number;
  /** 지금 칸(= done 번째 칸) 표시 여부. 기본 true. 모두 끝났거나 아직 시작 전이면 false */
  current?: boolean;
  /** 지금 칸 색. danger=빨강 · warning=노랑 · default=기본 회색 */
  tone?: StageBarTone;
  /** 스크린리더용 이름. 없으면 `total단계 중 done단계 끝남` */
  label?: string;
}

const clampInt = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, Number.isFinite(n) ? Math.trunc(n) : min));

export function StageBar({ total, done, current = true, tone = 'default', label, className = '', ...props }: StageBarProps) {
  const count = clampInt(total, 1, 99);
  const doneCount = clampInt(done, 0, count);
  const hasCurrent = current && doneCount < count;
  const text = label ?? `${count}단계 중 ${doneCount}단계 끝남`;
  return (
    <span className={cx('stage-bar', className)} role="img" aria-label={text} {...props}>
      {Array.from({ length: count }, (_, i) => {
        const state = i < doneCount ? 'done' : hasCurrent && i === doneCount ? 'current' : 'todo';
        return (
          <span
            key={i}
            aria-hidden="true"
            className={cx('stage-bar-cell', `is-${state}`, state === 'current' && `is-${tone}`)}
          />
        );
      })}
    </span>
  );
}
